<template>
  <div class="alerts-page">
    <div class="card">
      <div class="card-header">
        <h5 class="card-title">
          Alertas do Sistema
          <button class="btn btn-sm btn-primary float-right" @click="loadAlerts">
            <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
            Atualizar
          </button>
        </h5>
      </div>
      
      <div class="card-body">
        <!-- Filtros -->
        <div class="row mb-4">
          <div class="col-md-3">
            <select v-model="severityFilter" class="form-control form-control-sm">
              <option value="">Todas as severidades</option>
              <option value="critical">Crítico</option>
              <option value="warning">Aviso</option>
              <option value="info">Info</option>
            </select>
          </div>
          
          <div class="col-md-3">
            <select v-model="typeFilter" class="form-control form-control-sm">
              <option value="">Todos os tipos</option>
              <option value="disk">Disco</option>
              <option value="ram">RAM</option>
              <option value="cpu">CPU</option>
              <option value="application">Aplicação</option>
            </select>
          </div>
          
          <div class="col-md-3">
            <input 
              v-model="serverFilter" 
              type="text" 
              class="form-control form-control-sm" 
              placeholder="Filtrar por servidor..."
            >
          </div>
          
          <div class="col-md-3">
            <div class="d-flex align-items-center">
              <input 
                v-model="autoRefresh" 
                type="checkbox" 
                id="autoRefresh" 
                class="mr-2"
              >
              <label for="autoRefresh" class="mb-0 text-sm">Auto-refresh (30s)</label>
            </div>
          </div>
        </div>
        
        <!-- Estatísticas -->
        <div class="row mb-4">
          <div class="col-md-3">
            <div class="stat-card bg-danger text-white">
              <div class="stat-number">{{ criticalCount }}</div>
              <div class="stat-label">Críticos</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-card bg-warning text-white">
              <div class="stat-number">{{ warningCount }}</div>
              <div class="stat-label">Avisos</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-card bg-info text-white">
              <div class="stat-number">{{ infoCount }}</div>
              <div class="stat-label">Informativos</div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="stat-card bg-secondary text-white">
              <div class="stat-number">{{ filteredAlerts.length }}</div>
              <div class="stat-label">Total Filtrado</div>
            </div>
          </div>
        </div>
        
        <!-- Lista de Alertas -->
        <div v-if="filteredAlerts.length === 0" class="text-center text-muted py-5">
          <i class="fa fa-check-circle fa-4x mb-3"></i>
          <h5>Nenhum alerta encontrado</h5>
          <p>Todos os sistemas estão funcionando normalmente ou não há alertas que correspondam aos filtros aplicados.</p>
        </div>
        
        <div v-else class="alerts-container">
          <div 
            v-for="alert in paginatedAlerts" 
            :key="alert.id || `${alert.type}-${alert.server}-${alert.timestamp}`"
            class="alert-card mb-3"
            :class="getAlertCardClass(alert.severity)"
          >
            <div class="row align-items-center">
              <div class="col-md-1 text-center">
                <i :class="getAlertIcon(alert.type)" class="fa-2x"></i>
              </div>
              
              <div class="col-md-8">
                <div class="d-flex align-items-center mb-2">
                  <h6 class="mb-0 mr-3">{{ alert.server }}</h6>
                  <span 
                    class="badge" 
                    :class="getSeverityBadgeClass(alert.severity)"
                  >
                    {{ getSeverityLabel(alert.severity) }}
                  </span>
                  <span class="badge badge-light ml-2">
                    {{ getTypeLabel(alert.type) }}
                  </span>
                </div>
                
                <p class="mb-1">{{ alert.message }}</p>
                
                <div class="d-flex align-items-center text-muted">
                  <small class="mr-3">
                    <i class="fa fa-clock-o mr-1"></i>
                    {{ formatDate(alert.timestamp) }}
                  </small>
                  
                  <small v-if="alert.application" class="mr-3">
                    <i class="fa fa-code mr-1"></i>
                    {{ alert.application }}
                  </small>
                </div>
              </div>
              
              <div class="col-md-3 text-right">
                <div v-if="alert.value" class="alert-value mb-2">
                  <span class="h4" :class="getValueClass(alert.value, alert.type)">
                    {{ formatValue(alert.value, alert.type) }}
                  </span>
                </div>
                
                <router-link 
                  :to="`/servers/${getServerIdByHostname(alert.server)}`" 
                  class="btn btn-sm btn-outline-primary"
                  v-if="getServerIdByHostname(alert.server)"
                >
                  <i class="fa fa-eye mr-1"></i>
                  Ver Servidor
                </router-link>
              </div>
            </div>
          </div>
          
          <!-- Paginação -->
          <div v-if="totalPages > 1" class="d-flex justify-content-center mt-4">
            <nav>
              <ul class="pagination pagination-sm">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="currentPage = 1" :disabled="currentPage === 1">
                    <i class="fa fa-angle-double-left"></i>
                  </button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <button class="page-link" @click="currentPage--" :disabled="currentPage === 1">
                    <i class="fa fa-angle-left"></i>
                  </button>
                </li>
                
                <li 
                  v-for="page in visiblePages" 
                  :key="page"
                  class="page-item" 
                  :class="{ active: page === currentPage }"
                >
                  <button class="page-link" @click="currentPage = page">
                    {{ page }}
                  </button>
                </li>
                
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="currentPage++" :disabled="currentPage === totalPages">
                    <i class="fa fa-angle-right"></i>
                  </button>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <button class="page-link" @click="currentPage = totalPages" :disabled="currentPage === totalPages">
                    <i class="fa fa-angle-double-right"></i>
                  </button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AlertsPage',
  data() {
    return {
      loading: false,
      alerts: [],
      servers: [],
      severityFilter: '',
      typeFilter: '',
      serverFilter: '',
      autoRefresh: true,
      refreshInterval: null,
      currentPage: 1,
      itemsPerPage: 10
    }
  },
  computed: {
    filteredAlerts() {
      let filtered = this.alerts
      
      if (this.severityFilter) {
        filtered = filtered.filter(alert => alert.severity === this.severityFilter)
      }
      
      if (this.typeFilter) {
        filtered = filtered.filter(alert => alert.type === this.typeFilter)
      }
      
      if (this.serverFilter) {
        const search = this.serverFilter.toLowerCase()
        filtered = filtered.filter(alert => 
          alert.server.toLowerCase().includes(search)
        )
      }
      
      return filtered.sort((a, b) => {
        // Ordena por severidade e depois por timestamp
        const severityOrder = { critical: 3, warning: 2, info: 1 }
        const severityDiff = severityOrder[b.severity] - severityOrder[a.severity]
        
        if (severityDiff !== 0) {
          return severityDiff
        }
        
        return new Date(b.timestamp) - new Date(a.timestamp)
      })
    },
    
    paginatedAlerts() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredAlerts.slice(start, end)
    },
    
    totalPages() {
      return Math.ceil(this.filteredAlerts.length / this.itemsPerPage)
    },
    
    visiblePages() {
      const pages = []
      const start = Math.max(1, this.currentPage - 2)
      const end = Math.min(this.totalPages, this.currentPage + 2)
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    },
    
    criticalCount() {
      return this.filteredAlerts.filter(alert => alert.severity === 'critical').length
    },
    
    warningCount() {
      return this.filteredAlerts.filter(alert => alert.severity === 'warning').length
    },
    
    infoCount() {
      return this.filteredAlerts.filter(alert => alert.severity === 'info').length
    }
  },
  mounted() {
    this.loadAlerts()
    this.loadServers()
    
    if (this.autoRefresh) {
      this.startAutoRefresh()
    }
  },
  beforeDestroy() {
    this.stopAutoRefresh()
  },
  watch: {
    autoRefresh(newValue) {
      if (newValue) {
        this.startAutoRefresh()
      } else {
        this.stopAutoRefresh()
      }
    }
  },
  methods: {
    async loadAlerts() {
      this.loading = true
      try {
        const response = await this.$http.get(this.$store.getters.api + 'server-monitoring/alerts')
        this.alerts = response.data.data
      } catch (error) {
        console.error('Erro ao carregar alertas:', error)
        this.$toast.error('Erro ao carregar alertas')
      } finally {
        this.loading = false
      }
    },
    
    async loadServers() {
      try {
        const response = await this.$http.get(this.$store.getters.api + 'server-monitoring/servers')
        this.servers = response.data.data
      } catch (error) {
        console.error('Erro ao carregar servidores:', error)
      }
    },
    
    startAutoRefresh() {
      this.refreshInterval = setInterval(() => {
        this.loadAlerts()
      }, 30000)
    },
    
    stopAutoRefresh() {
      if (this.refreshInterval) {
        clearInterval(this.refreshInterval)
        this.refreshInterval = null
      }
    },
    
    getServerIdByHostname(hostname) {
      const server = this.servers.find(s => s.hostname === hostname)
      return server ? server.id : null
    },
    
    getAlertCardClass(severity) {
      switch (severity) {
        case 'critical':
          return 'alert-card-critical'
        case 'warning':
          return 'alert-card-warning'
        default:
          return 'alert-card-info'
      }
    },
    
    getAlertIcon(type) {
      switch (type) {
        case 'disk':
          return 'fa fa-hdd-o text-warning'
        case 'ram':
          return 'fa fa-memory text-danger'
        case 'cpu':
          return 'fa fa-microchip text-info'
        case 'application':
          return 'fa fa-code text-primary'
        default:
          return 'fa fa-exclamation-triangle text-warning'
      }
    },
    
    getSeverityBadgeClass(severity) {
      switch (severity) {
        case 'critical':
          return 'badge-danger'
        case 'warning':
          return 'badge-warning'
        default:
          return 'badge-info'
      }
    },
    
    getSeverityLabel(severity) {
      switch (severity) {
        case 'critical':
          return 'Crítico'
        case 'warning':
          return 'Aviso'
        default:
          return 'Info'
      }
    },
    
    getTypeLabel(type) {
      switch (type) {
        case 'disk':
          return 'Disco'
        case 'ram':
          return 'RAM'
        case 'cpu':
          return 'CPU'
        case 'application':
          return 'Aplicação'
        default:
          return 'Sistema'
      }
    },
    
    getValueClass(value, type) {
      if (type === 'disk' && value >= 80) return 'text-danger'
      if (type === 'ram' && value >= 90) return 'text-danger'
      if (type === 'cpu' && value >= 85) return 'text-danger'
      if (type === 'application' && value > 100) return 'text-danger'
      
      return 'text-warning'
    },
    
    formatValue(value, type) {
      if (type === 'disk' || type === 'ram' || type === 'cpu') {
        return `${value}%`
      }
      if (type === 'application' && typeof value === 'number') {
        return `${value}MB`
      }
      return value
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleString('pt-BR')
    }
  }
}
</script>

<style scoped>
.alerts-page {
  padding: 20px;
}

.stat-card {
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: bold;
}

.stat-label {
  font-size: 0.875rem;
  opacity: 0.9;
}

.alert-card {
  padding: 20px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: white;
  transition: all 0.2s ease;
}

.alert-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.alert-card-critical {
  border-left: 4px solid #dc3545;
}

.alert-card-warning {
  border-left: 4px solid #ffc107;
}

.alert-card-info {
  border-left: 4px solid #17a2b8;
}

.alert-value {
  text-align: center;
}

.text-sm {
  font-size: 0.875rem;
}

.fa-memory::before {
  content: "\f2db";
}

.fa-microchip::before {
  content: "\f2db";
}
</style> 